import React, { useCallback, useMemo, useState } from 'react';
import { compose } from 'utils/compose';
import { inject, observer } from 'mobx-react';
import OtpInput from 'react-otp-input';
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  HighlightOff as HighlightOffIcon,
  Drafts as DraftsIcon,
  PhoneIphone as PhoneIphoneIcon,
} from '@mui/icons-material';
import { Box, Checkbox, DialogContent, DialogTitle, FormControlLabel, TextField, Typography } from '@mui/material';

import Header from 'components/Header';
import { DialogActionButton, DialogFooter } from 'components/Dialog';

import withMediaQuery from 'constants/mediaQuery';
import menuItems from 'constants/menuItems';

import { VERIFICATION } from 'constants/verification';
import { setCookie } from 'utils/cookie';

import { useStyles } from '../styles';
import { maskMiddleWithThreeAsterisks, convertLabelPodMethods } from '../../../utils/common';

function DialogStepTwo({ store, mediaQuery, closeDialog, handleNext, handleClose, setHasToken, isDisplayMenu }) {
  const classes = useStyles();
  const [data, setData] = useState({ disableNext: true, policyAccepted: false, code: [] });
  const [errorMessage, setErrorMessage] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const { isRequested } = store.podStore;

  const nextStep = () => {
    if (typeof handleNext === 'function') {
      setErrorMessage('');
      store.deliveryStore.requestToken(store.deliveryStore.code, (res) => {
        if (res.ok) {
          setHasToken?.(true);
          setCookie('axl_shipment_token', store.deliveryStore.token, 0.003472);
          if (isRequested) store.deliveryStore.requestPOD();
          if (store.isViewPins) {
            handleNext?.(6);
          } else {
            handleNext?.(3);
          }
        } else {
          setHasToken?.(false);
          setErrorMessage(res?.data?.message || 'Verification failed. Please check your information and try again.');
        }
      });
      return;
    }

    if (store.isViewPins) {
      store.deliveryStore.setStep(6);
    }
    store.deliveryStore.processNextStep();
  };

  const handleCodeInput = (value) => {
    setOtpValue(value);
    store.deliveryStore.inputtingCode(value);
    setData({ ...data, disableNext: true });
    setErrorMessage('');
    // Auto-verify when all 6 digits are entered
    if (value.length === 6) {
      store.deliveryStore.verifyCode((isVerified) => setData((prev) => ({ ...prev, disableNext: !isVerified })));
    }
  };

  const resendCode = (e) => {
    e.preventDefault();
    store.deliveryStore.requestCode();
  };

  const changeVerificationMethod = (e) => {
    e.preventDefault();
    const { deliveryStore } = store;
    const { verificationMethod } = deliveryStore;

    const newMethod = verificationMethod === VERIFICATION.SMS ? VERIFICATION.EMAIL : VERIFICATION.SMS;
    deliveryStore.setVerificationMethod(newMethod);
    deliveryStore.requestCode();
  };

  const { disableNext, policyAccepted } = data;
  const { delivery, verificationMethod, verifyingCode, codeVerified, dialogLoading } = store.deliveryStore;

  const inputColor = useMemo(() => {
    if (codeVerified === true) return '#4caf50';
    if (codeVerified === false) return '#ba1a1a';
    return undefined;
  }, [codeVerified]);

  const inputStyle = useMemo(
    () => ({
      width: mediaQuery.isMobile ? 30 : 40,
      height: mediaQuery.isMobile ? 30 : 40,
      margin: '0 4px',
      fontSize: '20px',
      border: 'none',
      borderBottom: `1px solid ${inputColor || 'rgba(0,0,0,0.3)'}`,
      color: inputColor || 'inherit',
      outline: 'none',
    }),
    [mediaQuery.isMobile, inputColor]
  );

  const renderOtpInput = useCallback((props) => <input {...props} disabled={props.disabled || verifyingCode} />, [verifyingCode]);

  if (!delivery) return null;

  const { shipment } = delivery;

  return (
    <Box>
      {mediaQuery.isMobile && isDisplayMenu && <Header menuItems={menuItems} />}
      <DialogTitle>{isRequested ? <strong>Request POD</strong> : <strong>Please help us to verify it's you!</strong>}</DialogTitle>
      {(verificationMethod === VERIFICATION.SMS || verificationMethod === VERIFICATION.EMAIL) && (
        <DialogContent>
          <Typography sx={classes.darkGrayText}>
            We sent a verification code
            {verificationMethod === 'sms' ? (
              <Box component="span">
                {' '}
                in a SMS message via your phone <PhoneIphoneIcon sx={classes.inlineIcon} />
              </Box>
            ) : (
              <Box component="span">
                {' '}
                via your email address <DraftsIcon sx={classes.inlineIcon} />
              </Box>
            )}
          </Typography>
          <Box py={1} mt={3} mb={2}>
            <span style={{ fontWeight: 600 }}>Enter verification code</span>
          </Box>
          {errorMessage && (
            <Box mb={2}>
              <Typography color="error" variant="body2">
                {errorMessage}
              </Typography>
            </Box>
          )}
          <Box>
            <Box display="flex" alignItems="center" data-testid="otp-input-container">
              {!dialogLoading ? (
                <OtpInput
                  value={otpValue}
                  onChange={handleCodeInput}
                  numInputs={6}
                  inputType="tel"
                  shouldAutoFocus
                  containerStyle={classes.codeInput}
                  inputStyle={inputStyle}
                  renderInput={renderOtpInput}
                />
              ) : (
                <Box style={{ height: 60, padding: 2 }} /> // placeholder
              )}
              {!!codeVerified && <CheckCircleOutlineIcon sx={{ ...classes.greenIcon, ...classes.codeStatusIcon }} />}
              {codeVerified === false && <HighlightOffIcon sx={{ ...classes.redIcon, ...classes.codeStatusIcon }} />}
            </Box>
            <Box component="ul" sx={{ paddingLeft: mediaQuery.isMobile ? '16px' : undefined }}>
              <Box component="li" sx={{ ...classes.darkGrayText, ...classes.hint }}>
                Don't receive your code? Click{' '}
                <a href="#code" onClick={(e) => resendCode(e)}>
                  here
                </a>{' '}
                to send again!
              </Box>
              {shipment.customer.phone_number && shipment.customer.email && (
                <Box component="li" sx={{ ...classes.darkGrayText, ...classes.hint }}>
                  Change verification method? Click{' '}
                  <a href="#method" onClick={(e) => changeVerificationMethod(e)}>
                    here
                  </a>{' '}
                  to send verification code
                  {verificationMethod === 'sms' ? (
                    <Box component="span">
                      {' '}
                      via your email address <DraftsIcon sx={classes.inlineIcon} />
                    </Box>
                  ) : (
                    <Box component="span">
                      {' '}
                      in a SMS message via your phone <PhoneIphoneIcon sx={classes.inlineIcon} />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={policyAccepted}
                  onChange={(e) => setData({ ...data, policyAccepted: e.target.checked })}
                  value="agreed_policy"
                  color="primary"
                />
              }
              label={
                <Box component="span" sx={classes.darkGrayText}>
                  I agree to Jitsu's
                  <a href="https://gojitsu.com/privacy" target="_blank" rel="noopener noreferrer" style={{ marginLeft: '4.5px' }}>
                    Privacy Policy
                  </a>
                </Box>
              }
            />
          </Box>
        </DialogContent>
      )}
      {store.podStore.isPODFeedback && verificationMethod !== VERIFICATION.SMS && verificationMethod !== VERIFICATION.EMAIL && (
        <DialogContent>
          <Typography sx={classes.darkGrayText}>Please provide your shipment information to verify your identity.</Typography>
          <Box py={1} mt={3} mb={2}>
            <strong>Enter shipment information</strong>
          </Box>
          {errorMessage && (
            <Box mb={2}>
              <Typography color="error" variant="body2">
                {errorMessage}
              </Typography>
            </Box>
          )}
          {(() => {
            const fields = verificationMethod.split('|');

            return fields.map((field) => {
              const fieldLabel = convertLabelPodMethods(field);

              let handleInput;
              handleInput = (value) => {
                store.deliveryStore.userInputData[field] = value;
                setData({ ...data, [field]: value, disableNext: !value });
                setErrorMessage('');
              };

              return (
                <Box mb={2} key={field}>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={data[field] || ''}
                    onChange={(e) => handleInput(e.target.value)}
                    label={fieldLabel}
                    placeholder={field === 'house_number' ? 'E.g. 123 in 123 Main St.' : maskMiddleWithThreeAsterisks(shipment[field]) || fieldLabel}
                  />
                </Box>
              );
            });
          })()}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={policyAccepted}
                  onChange={(e) => setData({ ...data, policyAccepted: e.target.checked })}
                  value="agreed_policy"
                  color="primary"
                />
              }
              label={
                <Box component="span" sx={classes.darkGrayText}>
                  I agree to Jitsu's
                  <a href="https://gojitsu.com/privacy" target="_blank" rel="noopener noreferrer" style={{ marginLeft: '4.5px' }}>
                    Privacy Policy
                  </a>
                </Box>
              }
            />
          </Box>
        </DialogContent>
      )}
      <DialogFooter row>
        <DialogActionButton
          onClick={() => {
            closeDialog?.();
            handleClose?.();
          }}
          variant="outlined"
          color="primary"
          disableElevation
        >
          Cancel
        </DialogActionButton>
        <DialogActionButton onClick={nextStep} disabled={disableNext || !policyAccepted} variant="contained" color="primary" disableElevation>
          Confirm
        </DialogActionButton>
      </DialogFooter>
    </Box>
  );
}

const DialogStepTwoCompose = compose(inject('store'), observer)(DialogStepTwo);

export default withMediaQuery()(DialogStepTwoCompose);
